// src/hooks/useEnerjiOdasiPreview.js
import { useCallback, useRef } from 'react';
import * as THREE from 'three';
import { useTemplatePreviewScene } from './useTemplatePreviewScene';
import {
  drawClippedRoom,
  drawPanelRect,
  drawPanelDimensions,
  drawCustomDimensions,
  drawDimFirstPointMarker,
  drawDimPreview,
  drawCornerMarkers,
  drawSnapIndicator,
  drawDocumentFrame,
  drawEnergyRoomHeader,
} from '../utils/energyRoomDrawing';
import { panelIntersectsWalls } from '../shared/snap/wallSnapUtils';

export function useEnerjiOdasiPreview(containerRef) {
  const { scene, camera, renderer, clearScene, fitToContent } = useTemplatePreviewScene(containerRef);
  const floorPlanCloneRef = useRef(null);
  const selectionRectRef = useRef(null);

  const renderFloorPlan = useCallback((floorPlanGroup) => {
    if (!scene.current || !floorPlanGroup) return;
    clearScene();
    const clone = new THREE.Group();
    floorPlanGroup.traverse((obj) => {
      if ((obj.layers.mask & 2) !== 0) return;
      if (obj.isLine || obj.isLineSegments || obj.isMesh) {
        try {
          const c = obj.clone();
          obj.updateWorldMatrix(true, false);
          c.applyMatrix4(obj.matrixWorld);
          clone.add(c);
        } catch (e) { /* skip */ }
      }
    });
    scene.current.add(clone);
    floorPlanCloneRef.current = clone;
    setTimeout(() => fitToContent(), 50);
  }, [scene, clearScene, fitToContent]);

  /**
   * @param {number} drawingScale - e.g., 50 for 1:50
   * @param {object} dimCfg - { textSize, arrowSize, extInner, extOuter, lineSpacing } in cm
   */
  /**
   * @param {object} opts - Optional extra drawing params
   * @param {Array} opts.customDimensions - User-added dimension lines
   * @param {object|null} opts.customDimCfg - { textSize, arrowSize } for custom dims (cm)
   * @param {object|null} opts.dimP1 - First point marker
   * @param {string|null} opts.dimPhase - 'pickSecond' | 'pickOffset' | null
   * @param {object|null} opts.dimP2 - Second point (for pickOffset phase)
   * @param {object|null} opts.dimPreview - Current mouse world position for preview
   * @param {Array|null} opts.corners - Corner markers (shown in dimensionMode)
   * @param {object|null} opts.snapIndicator - {x, y, type} active snap indicator
   */
  const renderClippedRoom = useCallback((clippedSegments, panels, clipRect, drawingScale, selectedPanelId, dimCfg, skipFit = false, opts = {}) => {
    if (!scene.current) return;
    clearScene();

    drawClippedRoom(scene.current, clippedSegments);

    for (const panel of panels) {
      const intersects = panelIntersectsWalls(panel, clippedSegments, drawingScale);
      drawPanelRect(scene.current, panel, panel.id === selectedPanelId, drawingScale, intersects);
    }

    if (panels.length > 0 && dimCfg) {
      drawPanelDimensions(scene.current, panels, drawingScale, dimCfg);
    }

    // Custom dimension lines (use customDimCfg if available)
    const cdCfg = opts.customDimCfg || dimCfg;
    if (opts.customDimensions && opts.customDimensions.length > 0 && cdCfg) {
      drawCustomDimensions(scene.current, opts.customDimensions, drawingScale, cdCfg);
    }

    // First point marker
    if (opts.dimP1) {
      drawDimFirstPointMarker(scene.current, opts.dimP1);
    }

    // Dimension preview (live line or offset preview)
    if (opts.dimPhase && opts.dimP1 && opts.dimPreview && cdCfg) {
      drawDimPreview(scene.current, opts.dimPhase, opts.dimP1, opts.dimP2 || null, opts.dimPreview, drawingScale, cdCfg, opts.dimOrthoAxis);
    }

    // Corner snap markers (only in dimension mode)
    if (opts.corners && opts.corners.length > 0) {
      drawCornerMarkers(scene.current, opts.corners);
    }

    // Active snap indicator
    if (opts.snapIndicator) {
      drawSnapIndicator(scene.current, opts.snapIndicator, drawingScale);
    }

    // Document frame (drawn after all content, before fitToContent)
    if (opts.frameMargin) {
      drawDocumentFrame(scene.current, scene.current, opts.frameMargin, drawingScale);
    }

    // Header band above the frame
    drawEnergyRoomHeader(scene.current, scene.current, drawingScale);

    if (!skipFit) {
      setTimeout(() => fitToContent(), 50);
    } else if (renderer.current) {
      renderer.current.render(scene.current, camera.current);
    }
  }, [scene, camera, renderer, clearScene, fitToContent]);

  const updateSelectionRect = useCallback((startWorld, endWorld) => {
    if (!scene.current) return;
    if (selectionRectRef.current) {
      scene.current.remove(selectionRectRef.current);
      selectionRectRef.current.traverse((child) => {
        if (child.geometry) child.geometry.dispose();
        if (child.material) child.material.dispose();
      });
    }
    if (!startWorld || !endWorld) return;
    const minX = Math.min(startWorld.x, endWorld.x);
    const maxX = Math.max(startWorld.x, endWorld.x);
    const minY = Math.min(startWorld.y, endWorld.y);
    const maxY = Math.max(startWorld.y, endWorld.y);
    const w = maxX - minX;
    const h = maxY - minY;
    if (w < 0.01 || h < 0.01) return;

    const group = new THREE.Group();
    const geo = new THREE.PlaneGeometry(w, h);
    const mat = new THREE.MeshBasicMaterial({ color: 0x4fc3f7, transparent: true, opacity: 0.15, side: THREE.DoubleSide });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.position.set(minX + w / 2, minY + h / 2, 0.05);
    group.add(mesh);

    const borderPts = [
      new THREE.Vector3(minX, minY, 0.06), new THREE.Vector3(maxX, minY, 0.06),
      new THREE.Vector3(maxX, maxY, 0.06), new THREE.Vector3(minX, maxY, 0.06),
      new THREE.Vector3(minX, minY, 0.06),
    ];
    group.add(new THREE.Line(
      new THREE.BufferGeometry().setFromPoints(borderPts),
      new THREE.LineBasicMaterial({ color: 0x4fc3f7 })
    ));

    scene.current.add(group);
    selectionRectRef.current = group;
  }, [scene]);

  const screenToWorld = useCallback((clientX, clientY, canvasRect) => {
    if (!camera.current) return null;
    const ndcX = ((clientX - canvasRect.left) / canvasRect.width) * 2 - 1;
    const ndcY = -((clientY - canvasRect.top) / canvasRect.height) * 2 + 1;
    const vec = new THREE.Vector3(ndcX, ndcY, 0);
    vec.unproject(camera.current);
    return { x: vec.x, y: vec.y };
  }, [camera]);

  return {
    scene, camera, renderer,
    clearScene, fitToContent,
    renderFloorPlan, renderClippedRoom,
    updateSelectionRect, screenToWorld,
  };
}
